import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom'; // Adicione este import

// Imports do FullCalendar
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';

// --- Componente do Modal para exibir os detalhes do evento ---
const EventoModal = ({ evento, onClose }) => {
    if (!evento) return null;

    // Adapta para data_evento + hora separados, exibindo UTC
    const formatarData = (data, hora) => {
        if (!data) return '';
        let dateTimeStr = data;
        if (hora) {
            dateTimeStr = `${data}T${hora}Z`;
        }
        const d = new Date(dateTimeStr);
        if (isNaN(d.getTime())) return '';
        const diaUTC = String(d.getUTCDate()).padStart(2, '0');
        const mesUTC = String(d.getUTCMonth() + 1).padStart(2, '0');
        const anoUTC = d.getUTCFullYear();
        const horaUTC = String(d.getUTCHours()).padStart(2, '0');
        const minUTC = String(d.getUTCMinutes()).padStart(2, '0');
        return `${diaUTC}/${mesUTC}/${anoUTC} ${horaUTC}:${minUTC}`;
    };

    // Extrai data_evento e hora do evento, se existirem
    const dataEvento = evento.data_evento || evento.start;
    const horaEvento = evento.hora || (evento.start ? new Date(evento.start).toISOString().substring(11, 19) : undefined);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-2xl font-bold text-indigo-800 mb-4">{evento.title}</h2>
                <div className="space-y-4 text-slate-700">
                    <p className="flex items-start"><strong className="w-32 flex-shrink-0 font-semibold">🗓️ Data e Hora:</strong> <span>{formatarData(dataEvento, horaEvento)}</span></p>
                    {evento.extendedProps.local && (
                        <p className="flex items-start"><strong className="w-32 flex-shrink-0 font-semibold">📍 Local:</strong> <span>{evento.extendedProps.local}</span></p>
                    )}
                    {evento.extendedProps.descricao && (
                        <p className="flex items-start"><strong className="w-32 flex-shrink-0 font-semibold">📄 Descrição:</strong> <span>{evento.extendedProps.descricao}</span></p>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="mt-8 w-full bg-indigo-600 text-white py-2.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Fechar
                </button>
            </div>
        </div>
    );
};


// --- Componente Principal da Página ---
export default function Reunioes() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const navigate = useNavigate();

    // Recupera user e grupo_id do localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const grupoId = user?.grupo_id;

    // Função para buscar os eventos da agenda já filtrados pelo back-end
    const fetchAgendas = useCallback(async () => {
        setLoading(true);
        setError(null);

        if (!grupoId) {
            setError('Não foi possível identificar o grupo do usuário. Faça login novamente.');
            setLoading(false);
            return;
        }

        try {
            const response = await api.get(`/agenda/grupo/${grupoId}`);
            const formattedEvents = response.data.map(evento => ({
                id: evento.id,
                title: evento.titulo,
                start: evento.data_evento,
                extendedProps: {
                    descricao: evento.descricao,
                    local: evento.local,
                    data_evento: evento.data_evento,
                    hora: evento.hora
                }
            }));
            setEvents(formattedEvents);
        } catch (err) {
            // erro já tratado abaixo
            const errorMessage = typeof err.response?.data?.error === 'string'
                ? err.response.data.error
                : 'Não foi possível carregar a agenda no momento.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [grupoId]);

    useEffect(() => {
        fetchAgendas();
    }, [fetchAgendas]);

    const handleEventClick = (clickInfo) => {
        // Garante que data_evento e hora sejam passados para o modal
        setSelectedEvent({
            title: clickInfo.event.title,
            start: clickInfo.event.start,
            data_evento: clickInfo.event.extendedProps?.data_evento || clickInfo.event.start,
            hora: clickInfo.event.extendedProps?.hora,
            extendedProps: clickInfo.event.extendedProps
        });
    };

    return (
        <div className="p-4 md:p-8 bg-slate-100 min-h-screen">
            <style>{`.animate-fade-in-scale { animation: fadeInScale 0.3s ease-out forwards; } @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
            
            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg mb-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-4">
                    <h1 className="text-2xl font-bold text-slate-800 px-2 flex-1">Agenda de Reuniões e Eventos</h1>
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg font-semibold shadow hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                        <span className="inline-block align-middle mr-2 text-xl" style={{ verticalAlign: 'middle', minWidth: '20px' }}>←</span>
                        Voltar para Home
                    </button>
                </div>
                {error && <div className="text-center p-4 mb-4 font-medium text-red-600 bg-red-50 rounded-lg">{error}</div>}
                
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                        <p className="text-sm text-slate-500 mt-3">Carregando agenda...</p>
                    </div>
                ) : (
                    <FullCalendar
                        plugins={[dayGridPlugin]}
                        initialView="dayGridMonth"
                        locale={ptBrLocale}
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,dayGridWeek'
                        }}
                        buttonText={{
                            today:    'Hoje',
                            month:    'Mês',
                            week:     'Semana'
                        }}
                        events={events}
                        eventClick={handleEventClick}
                        height="auto"
                        eventColor="#4F46E5"
                        eventDisplay="block"
                        dayMaxEvents={true}
                    />
                )}
            </div>
            
            <EventoModal evento={selectedEvent} onClose={() => setSelectedEvent(null)} />
        </div>
    );
}